"use client";

import {JSX, memo, useEffect, useId, useRef} from "react";

import {reinsertScripts} from "@/sections/content/html-section/utils";

const RAW_EMBED_ATTR = "data-raw-embed-id";

/** Extract height from iframe style in html string (e.g. style="...height:540px...") */
function getHeightFromHtmlStyle(html: string): number | null {
    const match = html.match(/height:\s*(\d+)px/i);
    if (!match) return null;
    const n = Number.parseInt(match[1], 10);
    return Number.isFinite(n) ? n : null;
}

type RawEmbedIframeMountProps = {
    id: string;
};

const RawEmbedIframeMount = memo(
    function RawEmbedIframeMount({id}: RawEmbedIframeMountProps): JSX.Element {
        return <div {...{[RAW_EMBED_ATTR]: id}} className="raw-embed" />;
    },
    (prev, next) => prev.id === next.id,
);

type RawEmbedSectionProps = {
    html?: string;
    css?: string;
    scopeKey: string;
    className?: string;
};

function simpleHash(str: string): string {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        const char = str.charCodeAt(i);
        hash = (hash << 5) - hash + char;
        hash |= 0;
    }
    return Math.abs(hash).toString(16).padStart(8, "0").slice(0, 8);
}

function RawEmbedSectionComponent({html, css, scopeKey, className}: RawEmbedSectionProps): JSX.Element {
    const scopeId = "section-" + simpleHash(scopeKey);
    const sectionRef = useRef<HTMLElement>(null);
    const embedId = useId();
    const hasSetHtmlRef = useRef(false);

    const getRawEmbedEl = (): HTMLDivElement | null => sectionRef.current?.querySelector<HTMLDivElement>(`[${RAW_EMBED_ATTR}="${embedId}"]`) ?? null;

    useEffect(() => {
        if (!html) return;
        const el = getRawEmbedEl();
        if (el && !hasSetHtmlRef.current) {
            hasSetHtmlRef.current = true;
            el.innerHTML = html;
        }
        if (sectionRef.current) {
            reinsertScripts(sectionRef.current);
        }
    }, [html]);

    const sectionMinHeight = html ? getHeightFromHtmlStyle(html) : null;

    const sanitizedCss = css
        ?.replace(/<\/?script[^>]*>/gi, "")
        .replace(/@import/gi, "")
        .replace(/javascript:/gi, "");

    const scopedCss = sanitizedCss
        ?.split("}")
        .filter((rule) => rule.trim())
        .map((rule) => {
            const trimmedRule = rule.trim();
            if (!trimmedRule) return "";
            const parts = trimmedRule.split("{");
            if (parts.length < 2) return "";
            const selector = parts[0].trim();
            const properties = parts.slice(1).join("{");
            return `.${scopeId} ${selector} { ${properties} }`;
        })
        .join("\n");

    return (
        <section
            ref={sectionRef}
            className={`raw-embed-iframe ${scopeId} ${className || ""}`}
            style={sectionMinHeight != null ? {minHeight: `${sectionMinHeight}px`} : undefined}
            suppressHydrationWarning
        >
            {scopedCss && <style suppressHydrationWarning dangerouslySetInnerHTML={{__html: scopedCss}} />}
            {html && <RawEmbedIframeMount id={embedId} />}
        </section>
    );
}

export const RawEmbedSection = memo(RawEmbedSectionComponent);
